import { type ReactNode } from 'react'
import * as Accordion from '@radix-ui/react-accordion'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'

const cardShadow =
  'shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'

const chevronBtnClass =
  'flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f4f5f8] text-[#171a20]'

const contentAnimClass =
  'overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down'

export function FormAccordion({
  type = 'multiple',
  defaultValue,
  className,
  children,
}: {
  type?: 'single' | 'multiple'
  defaultValue?: string | string[]
  className?: string
  children: ReactNode
}) {
  if (type === 'single') {
    const value = Array.isArray(defaultValue) ? defaultValue[0] : defaultValue
    return (
      <Accordion.Root
        type='single'
        collapsible
        defaultValue={value}
        className={className}
      >
        {children}
      </Accordion.Root>
    )
  }

  return (
    <Accordion.Root
      type='multiple'
      defaultValue={
        Array.isArray(defaultValue)
          ? defaultValue
          : defaultValue
            ? [defaultValue]
            : undefined
      }
      className={className}
    >
      {children}
    </Accordion.Root>
  )
}

export function FormAccordionCard({
  value,
  step,
  title,
  badge,
  complete,
  stepClassName,
  titleClassName,
  children,
}: {
  value: string
  step: number
  title: string
  badge?: ReactNode
  complete?: boolean
  stepClassName?: string
  titleClassName?: string
  children: ReactNode
}) {
  return (
    <Accordion.Item
      value={value}
      className={cn(
        'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white',
        cardShadow
      )}
    >
      <Accordion.Header>
        <Accordion.Trigger className='group flex w-full items-center gap-2 px-6 py-4 text-left'>
          <span
            className={cn(
              'flex size-6 shrink-0 items-center justify-center rounded-[6px] text-xs font-semibold leading-4 text-white',
              complete
                ? 'bg-[#10b981]'
                : 'bg-[#8f95a6] group-data-[state=open]:bg-[#ff5a1f]',
              stepClassName
            )}
          >
            {step}
          </span>
          <span
            className={cn(
              'min-w-0 flex-1 truncate font-heading text-sm font-bold uppercase leading-[1.2] text-[#171a20]',
              titleClassName
            )}
          >
            {title}
          </span>
          {badge}
          <span className={chevronBtnClass}>
            <ChevronDown className='size-4 group-data-[state=open]:hidden' />
            <ChevronUp className='hidden size-4 group-data-[state=open]:block' />
          </span>
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content className={contentAnimClass}>
        <div className='border-t border-[#e7e9ef] px-6 pb-6 pt-4'>
          {children}
        </div>
      </Accordion.Content>
    </Accordion.Item>
  )
}

export function FormInnerBlock({
  value,
  title,
  required,
  badge,
  titleClassName,
  children,
}: {
  value: string
  title: string
  required?: boolean
  badge?: ReactNode
  titleClassName?: string
  children: ReactNode
}) {
  return (
    <Accordion.Item
      value={value}
      className='overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white'
    >
      <Accordion.Header>
        <Accordion.Trigger className='group flex w-full items-center gap-2 px-6 pt-4 text-left data-[state=closed]:pb-4'>
          <span className='flex min-w-0 flex-1 items-center gap-2 border-l-4 border-[#ff5a1f] px-6'>
            <span
              className={cn(
                'font-heading text-base font-semibold leading-[1.2] text-[#171a20]',
                titleClassName
              )}
            >
              {title}
              {required && <span className='text-destructive'> *</span>}
            </span>
            {badge}
          </span>
          <span className={chevronBtnClass}>
            <ChevronDown className='size-4 group-data-[state=open]:hidden' />
            <ChevronUp className='hidden size-4 group-data-[state=open]:block' />
          </span>
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content className={contentAnimClass}>
        <div className='flex flex-col gap-4 px-6 pb-6 pt-4'>{children}</div>
      </Accordion.Content>
    </Accordion.Item>
  )
}

export function FormBadge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode
  tone?: 'neutral' | 'warning' | 'success'
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex h-6 shrink-0 items-center rounded-lg px-2 text-xs font-semibold leading-4',
        tone === 'success' && 'bg-[#d1fae5] text-[#065f46]',
        tone === 'warning' && 'bg-[#fef3c7] text-[#8a5405]',
        tone === 'neutral' && 'bg-[#f4f5f8] text-[#171a20]',
        className
      )}
    >
      {children}
    </span>
  )
}

export function FormFieldsCountBadge({
  count,
  label,
  completedLabel,
  className,
}: {
  count: number
  label: string
  completedLabel: string
  className?: string
}) {
  if (count <= 0) {
    return (
      <FormBadge tone='success' className={className}>
        {completedLabel}
      </FormBadge>
    )
  }
  return (
    <FormBadge tone='warning' className={className}>
      {label}
    </FormBadge>
  )
}

export function FormNeutralBadge({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <FormBadge className={className}>{children}</FormBadge>
}
